import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deletePersistedChunk,
  listPersistedChunks,
  putPersistedChunk,
  clearPersistedChunks,
  type PersistedChunk
} from '@lib/chunkQueueDB';

type UseStoreIndexedDBOptions = {
  appointmentId: number | null;
  recordingId: string | null | undefined;
};

export function useStoreIndexedDB({ appointmentId, recordingId }: UseStoreIndexedDBOptions) {
  const [persistedCount, setPersistedCount] = useState(0);
  const lastIdentityRef = useRef<string | null>(null);

  const refreshMetrics = useCallback(async () => {
    if (!recordingId) {
      setPersistedCount(0);
      return;
    }
    const chunks = await listPersistedChunks(recordingId);
    setPersistedCount(chunks.length);
  }, [recordingId]);

  const saveChunkToDisk = useCallback(
    async (blob: Blob, chunkIndex: number, mimeType: string) => {
      if (appointmentId == null || recordingId == null) return;

      const key = `${appointmentId}:${recordingId}:${chunkIndex}`;
      const item: PersistedChunk = {
        key,
        appointmentId,
        recordingId,
        chunkIndex,
        mimeType,
        blob,
        attempts: 0,
        createdAt: Date.now()
      };

      await putPersistedChunk(item);
      await refreshMetrics();
    },
    [appointmentId, recordingId, refreshMetrics]
  );

  const getOfflineChunks = useCallback(async () => {
    if (!recordingId) return [];
    return await listPersistedChunks(recordingId);
  }, [recordingId]);

  const markAsSynced = useCallback(
    async (key: string) => {
      await deletePersistedChunk(key);
      await refreshMetrics();
    },
    [refreshMetrics]
  );

  const purgeSession = useCallback(async () => {
    if (!recordingId) return;
    await clearPersistedChunks(recordingId);
    await refreshMetrics();
  }, [recordingId, refreshMetrics]);

  useEffect(() => {
    const identity = appointmentId != null && recordingId ? `${appointmentId}:${recordingId}` : null;
    if (identity === lastIdentityRef.current) return;
    lastIdentityRef.current = identity;

    refreshMetrics();
  }, [appointmentId, recordingId, refreshMetrics]);

  return {
    saveChunkToDisk,
    getOfflineChunks,
    markAsSynced,
    purgeSession,
    persistedCount
  };
}
